import { useState } from 'react';
import { C } from '../../lib/tokens.js';
import {
  login, verifyTwoFactor, beginTwoFactorEnrollment, confirmTwoFactorEnrollment, storeLoginMarker,
} from '../../lib/api.js';
import { useT } from '../../lib/i18n.jsx';

// ─── Minimal public header (no auth-required controls) ───────────────────────

function PublicHeader({ onLoginClick }) {
  const t = useT();
  return (
    <header style={{
      height: 52, background: C.tin, borderBottom: `1px solid ${C.mercury}`,
      display: 'flex', alignItems: 'center', padding: '0 24px', flex: 'none',
      position: 'sticky', top: 0, zIndex: 50,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <img src="assets/logo.svg" alt="ActFact" style={{ height: 22 }}
          onError={e => { e.target.style.display = 'none'; }} />
        <div style={{
          fontSize: 11, fontWeight: 500, color: C.liquid,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          paddingLeft: 8, borderLeft: `1px solid ${C.mercury}`,
        }}>{t('login.header.brand')}</div>
      </div>
      <div style={{ flex: 1 }} />
      <button
        type="button"
        onClick={onLoginClick}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 13, fontWeight: 700, color: C.deepsea, fontFamily: 'Roboto',
          padding: '6px 0',
        }}
      >
        {t('login.header.login')}
      </button>
    </header>
  );
}

// ─── Public info cards ────────────────────────────────────────────────────────

function InfoCard({ title, children }) {
  return (
    <div style={{
      background: C.tin, borderRadius: 4, padding: '16px 20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <div style={{
        display: 'inline-block', fontSize: 12, color: C.liquid, fontWeight: 500,
        border: `1px solid ${C.sky}`, borderRadius: 3, padding: '2px 8px',
        marginBottom: 12,
      }}>{title}</div>
      {children}
    </div>
  );
}

function PublicInfoPage() {
  const t = useT();
  return (
    <div style={{ maxWidth: 800, margin: '32px auto', padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <InfoCard title={t('login.info.helpdesk.title')}>
        <p style={{ margin: 0, fontSize: 13, color: C.fg, lineHeight: 1.6 }}>
          {t('login.info.helpdesk.body')}
        </p>
      </InfoCard>

      <InfoCard title={t('login.info.availability.title')}>
        <p style={{ margin: 0, fontSize: 13, color: C.fg, lineHeight: 1.6 }}>
          {t('login.info.availability.body1')}{' '}
          <a href="mailto:service@actfact.com" style={{ color: C.liquid }}>service@actfact.com</a>
          {t('login.info.availability.body2')}
        </p>
      </InfoCard>
    </div>
  );
}

// ─── Login form ───────────────────────────────────────────────────────────────

function LoginForm({ onSuccess, locale, onLocaleChange }) {
  const t = useT();
  const [step, setStep]         = useState('credentials'); // 'credentials' | 'twofactor' | 'enroll'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode]         = useState('');
  const [pendingToken, setPendingToken] = useState(null);
  const [enrollData, setEnrollData] = useState(null); // { secret, qrDataUri }
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  // The session cookie is already set by the server's response - just record the login.
  function acceptLogin() {
    onSuccess(storeLoginMarker());
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!username.trim()) { setError(t('login.error.username_required')); return; }
    if (!password)        { setError(t('login.error.password_required')); return; }
    setError('');
    setLoading(true);
    try {
      const result = await login(username.trim(), password);
      if (result.status === 'mfa_required') {
        setPendingToken(result.pendingToken);
        setStep('twofactor');
      } else if (result.status === 'enrollment_required') {
        setPendingToken(result.pendingToken);
        try {
          const data = await beginTwoFactorEnrollment(result.pendingToken);
          setEnrollData(data);
          setStep('enroll');
        } catch (err) {
          setError(err.message || t('login.enroll.error.load_failed'));
        }
      } else {
        acceptLogin();
      }
    } catch (err) {
      setError(err.message || t('login.error.generic'));
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    if (!code.trim()) { setError(t('login.twofactor.error.code_required')); return; }
    setError('');
    setLoading(true);
    try {
      await verifyTwoFactor(pendingToken, code.trim());
      acceptLogin();
    } catch (err) {
      setError(err.message || t('login.error.generic'));
    } finally {
      setLoading(false);
    }
  }

  async function handleEnrollConfirm(e) {
    e.preventDefault();
    if (!code.trim()) { setError(t('login.enroll.error.code_required')); return; }
    setError('');
    setLoading(true);
    try {
      await confirmTwoFactorEnrollment(pendingToken, code.trim());
      acceptLogin();
    } catch (err) {
      setError(err.message || t('login.error.generic'));
    } finally {
      setLoading(false);
    }
  }

  function handleBack() {
    setStep('credentials');
    setPendingToken(null);
    setEnrollData(null);
    setCode('');
    setError('');
  }

  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 13,
    border: `1px solid ${C.mercury}`, borderRadius: 3,
    outline: 'none', fontFamily: 'Roboto', color: C.fg,
    background: loading ? C.platinum : C.tin,
    boxSizing: 'border-box',
  };

  const errorBanner = error && (
    <div style={{ fontSize: 12, color: C.rust, padding: '6px 10px', background: '#FFF5F6', border: `1px solid #FFD6DA`, borderRadius: 3 }}>
      {error}
    </div>
  );

  return (
    <div style={{ maxWidth: 800, margin: '32px auto', padding: '0 24px' }}>
      <div style={{
        background: C.tin, borderRadius: 4,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden',
      }}>
        {/* Tab bar */}
        <div style={{ borderBottom: `1px solid ${C.mercury}`, padding: '0 16px' }}>
          <div style={{
            display: 'inline-block', padding: '10px 12px',
            fontSize: 13, color: C.liquid, fontWeight: 500,
            borderBottom: `2px solid ${C.liquid}`, marginBottom: -1,
          }}>{t('login.tab')}</div>
        </div>

        {step === 'credentials' ? (
          <form onSubmit={handleSubmit} style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              type="text"
              autoComplete="username"
              placeholder={t('login.field.username')}
              value={username}
              onChange={e => setUsername(e.target.value)}
              disabled={loading}
              style={inputStyle}
            />
            <input
              type="password"
              autoComplete="current-password"
              placeholder={t('login.field.password')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={loading}
              style={inputStyle}
            />

            <select
              value={locale}
              onChange={e => onLocaleChange(e.target.value)}
              disabled={loading}
              style={inputStyle}
            >
              <option value="nl-NL">Nederlands</option>
              <option value="en-US">English</option>
            </select>

            {errorBanner}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
              marginTop: 4
            }}>
              <a href="mailto:service@actfact.com?subject=Wachtwoord%20vergeten"
                style={{ fontSize: 13, color: C.liquid, textDecoration: 'none' }}>
                {t('login.forgot')}
              </a>
              <button
                type="submit"
                disabled={loading}
                style={{
                  background: loading ? C.silver : C.liquid,
                  color: '#fff', border: 'none', padding: '8px 20px',
                  borderRadius: 3, fontSize: 13, fontWeight: 500,
                  cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Roboto',
                  transition: 'background 120ms',
                }}
              >
                {loading ? t('login.submit.loading') : t('login.submit')}
              </button>
            </div>
          </form>
        ) : step === 'twofactor' ? (
          <form onSubmit={handleVerify} style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: C.fg }}>{t('login.twofactor.title')}</div>
            <div style={{ fontSize: 13, color: C.liquid, marginTop: -8 }}>{t('login.twofactor.body')}</div>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder={t('login.twofactor.field.code')}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              disabled={loading}
              autoFocus
              style={inputStyle}
            />

            {errorBanner}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
              marginTop: 4
            }}>
              <button
                type="button"
                onClick={handleBack}
                disabled={loading}
                style={{
                  background: 'none', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 13, color: C.liquid, fontFamily: 'Roboto', padding: 0,
                }}
              >
                {t('login.twofactor.back')}
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  background: loading ? C.silver : C.liquid,
                  color: '#fff', border: 'none', padding: '8px 20px',
                  borderRadius: 3, fontSize: 13, fontWeight: 500,
                  cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Roboto',
                  transition: 'background 120ms',
                }}
              >
                {loading ? t('login.twofactor.submit.loading') : t('login.twofactor.submit')}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleEnrollConfirm} style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: C.fg }}>{t('login.enroll.title')}</div>
            <div style={{ fontSize: 13, color: C.liquid, marginTop: -8 }}>{t('login.enroll.body')}</div>

            {enrollData && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                <img
                  src={enrollData.qrDataUri}
                  alt="TOTP QR code"
                  style={{ width: 176, height: 176, border: `1px solid ${C.mercury}`, borderRadius: 3 }}
                />
                <div style={{ fontSize: 12, color: C.liquid }}>{t('login.enroll.secret_label')}</div>
                <div style={{
                  fontSize: 12, fontFamily: 'monospace', color: C.fg, letterSpacing: '0.05em',
                  wordBreak: 'break-all', textAlign: 'center',
                }}>
                  {enrollData.secret}
                </div>
              </div>
            )}

            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder={t('login.enroll.field.code')}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              disabled={loading}
              style={inputStyle}
            />

            {errorBanner}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
              marginTop: 4
            }}>
              <button
                type="button"
                onClick={handleBack}
                disabled={loading}
                style={{
                  background: 'none', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 13, color: C.liquid, fontFamily: 'Roboto', padding: 0,
                }}
              >
                {t('login.enroll.back')}
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  background: loading ? C.silver : C.liquid,
                  color: '#fff', border: 'none', padding: '8px 20px',
                  borderRadius: 3, fontSize: 13, fontWeight: 500,
                  cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Roboto',
                  transition: 'background 120ms',
                }}
              >
                {loading ? t('login.enroll.submit.loading') : t('login.enroll.submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Public shell (header + content) ─────────────────────────────────────────

export function LoginPage({ onLogin, locale, onLocaleChange }) {
  const [showLogin, setShowLogin] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: C.platinum }}>
      <PublicHeader onLoginClick={() => setShowLogin(true)} />
      {showLogin
        ? <LoginForm onSuccess={onLogin} locale={locale} onLocaleChange={onLocaleChange} />
        : <PublicInfoPage />
      }
    </div>
  );
}
